/**
 * Annexure B tier classifier — FE mirror of the BE
 * ``_compute_annexure_b_tier`` service in
 * ``backend/src/services/analysis/risk/synthesizer.py``.
 *
 * Sprint 5 client feedback 2026-07-09 follow-up 19 (Batch C) —
 * matrix reworked to Khalid Al Busaidi's golden reference (OIA
 * ISMS audit report, Annexure B):
 *
 *     Overall Rating               | High    | Medium
 *     Unacceptable Risk Exposure   | 3 or +  | 9 or +
 *     Major Improvement Opportunity| 2       | 6 to 8
 *     Some Improvement Opportunity | 1       | Up to 5
 *     Satisfactory                 | 1       | Up to 3
 *     Well-Managed                 | -       | 1
 *
 *     Notes:
 *       1. High is equivalent to 3 Medium risk observations.
 *       2. Low risk observations are NOT considered in the overall rating.
 *       3. Matrix is INDICATIVE only.
 *
 * Two structural changes vs the previous (pre-golden) matrix:
 *   * Critical column DROPPED — Critical severity maps into High
 *     for tier purposes. Critical count preserved on flag_composition
 *     for display only.
 *   * Low findings EXCLUDED from the tier — per Note 2. Low count
 *     preserved for display; classifier ignores it.
 *
 * Byte-equivalent to the Python source: same band edges, same tier
 * order, same tier → auditor-opinion + recommendation maps.
 */

export interface FlagCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface AnnexureBTier {
  tier_name: string;
  tier_index: number;
  auditor_opinion: string;
  recommendation: string;
  high_critical_sum: number;      // for display; effective H under golden matrix
  medium_low_sum: number;         // for display
  flag_composition: FlagCounts & { total: number };
  reviewer_override_applied: boolean;
  indicative: boolean;
}

const _TIER_ORDER: string[] = [
  'Well-Managed',                   // 0 — best
  'Satisfactory',                   // 1
  'Some Improvement Opportunity',   // 2
  'Major Improvement Opportunity',  // 3
  'Unacceptable Risk Exposure',     // 4 — worst
];

/* Sprint 5 client feedback 2026-07-12 (issues/11 follow-up) —
 * Auditor Assurance Opinion label now uses the OIA tier vocabulary
 * DIRECTLY (per Appendix B), not the legacy Ineffective / Partially
 * Effective / Improvement Required / Effective aliases. Client
 * confirmed on live AUDIT-2026-0146 that "Ineffective" reads as
 * inconsistent with the Appendix B rubric which uses the OIA tier
 * names. The Recommendation card in §1 similarly displays the OIA
 * tier name (was Reject / Under Review / Approved). Same string is
 * shown across the KPI tile, the auditor-opinion card, and the
 * Appendix B highlight — one vocabulary, one term. */
const _TIER_TO_AUDITOR_OPINION: Record<string, string> = {
  'Unacceptable Risk Exposure':    'Unacceptable Risk Exposure',
  'Major Improvement Opportunity': 'Major Improvement Opportunity',
  'Some Improvement Opportunity':  'Some Improvement Opportunity',
  'Satisfactory':                  'Satisfactory',
  'Well-Managed':                  'Well-Managed',
};

const _TIER_TO_RECOMMENDATION: Record<string, string> = {
  'Unacceptable Risk Exposure':    'Unacceptable Risk Exposure',
  'Major Improvement Opportunity': 'Major Improvement Opportunity',
  'Some Improvement Opportunity':  'Some Improvement Opportunity',
  'Satisfactory':                  'Satisfactory',
  'Well-Managed':                  'Well-Managed',
};


export function computeAnnexureBTier(
  counts: FlagCounts,
  reviewerOverrideTier?: string,
): AnnexureBTier {
  const c = counts.critical || 0;
  const h = counts.high || 0;
  const m = counts.medium || 0;
  const lo = counts.low || 0;

  // Golden ref: Critical rolls into High; Low is excluded.
  const hEffective = h + c;
  const mEffective = m;

  let tierName: string;
  if (hEffective >= 3 || mEffective >= 9) {
    tierName = 'Unacceptable Risk Exposure';
  } else if (hEffective === 2 || mEffective >= 6) {
    tierName = 'Major Improvement Opportunity';
  } else if (mEffective >= 4) {
    tierName = 'Some Improvement Opportunity';
  } else if (hEffective === 1 || mEffective >= 2) {
    tierName = 'Satisfactory';
  } else {
    tierName = 'Well-Managed';
  }

  let overrideApplied = false;
  if (reviewerOverrideTier && _TIER_ORDER.includes(reviewerOverrideTier)) {
    tierName = reviewerOverrideTier;
    overrideApplied = true;
  }

  const tierIndex = _TIER_ORDER.indexOf(tierName);

  return {
    tier_name: tierName,
    tier_index: tierIndex,
    auditor_opinion: _TIER_TO_AUDITOR_OPINION[tierName],
    recommendation: _TIER_TO_RECOMMENDATION[tierName],
    high_critical_sum: hEffective,
    medium_low_sum: m + lo,
    flag_composition: {
      critical: c, high: h, medium: m, low: lo,
      total: c + h + m + lo,
    },
    reviewer_override_applied: overrideApplied,
    indicative: true,
  };
}
